import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { trPositionShort } from "@/lib/pesTranslations";

const fmtM = (v) => `${(Number(v || 0) / 1e6).toFixed(0)} mln`;

const STRUCT_META = {
    athletic_trainer: { icon: "🏃", accent: "rgb(var(--tc-2bb6ff))" },
    medical_center: { icon: "➕", accent: "rgb(var(--tc-2ecc71))" },
};

export default function StructuresPanel({ teamId, reloadKey = 0, onChanged }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(null);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const load = useCallback(() => {
        if (!teamId) return;
        api.get(`/teams/${teamId}/structures`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message));
    }, [teamId]);

    useEffect(() => { load(); }, [load, reloadKey]);

    const buy = async (s) => {
        if (!window.confirm(`Acquistare "${s.label}"?\n\nCosto: ${fmtM(s.build_cost)} EUR (dal budget squadra → Cassa OP)\nManutenzione annuale: ${fmtM(s.annual_cost)}.`)) return;
        setBusy(`buy:${s.key}`); setErr(""); setMsg("");
        try {
            await api.post(`/teams/${teamId}/structures/buy`, { structure: s.key });
            setMsg(`${s.label} acquistata!`);
            load();
            onChanged && onChanged();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(null); }
    };

    const runMedical = async (inj) => {
        if (!window.confirm(`Usare il Centro Medico su ${inj.player_name}?\n\nRiduce l'infortunio di 1 giornata. Utilizzabile 1 volta per giornata di campionato.`)) return;
        setBusy(`med:${inj.id}`); setErr(""); setMsg("");
        try {
            const { data: r } = await api.post(`/teams/${teamId}/structures/medical-center/use`, { injury_id: inj.id });
            setMsg(r.healed ? `${inj.player_name} è rientrato dall'infortunio!` : `${inj.player_name}: infortunio ridotto di 1 giornata.`);
            load();
            onChanged && onChanged();
        } catch (e) {
            setErr(e.response?.data?.detail || e.message);
        } finally { setBusy(null); }
    };

    if (!data) return null;

    const { can_manage, club_dev_used_this_season, prereq_ok, prereq_label, summer_open, controlled_admin, budget, structures, medical_center, injuries, current_giornata } = data;
    const clubDevLocked = !!club_dev_used_this_season;

    return (
        <section className="op-card overflow-hidden mb-8" data-testid="structures-panel">
            <div className="px-5 py-3 border-b border-t22252a flex items-center gap-3">
                <span className="op-label" style={{ color: "rgb(var(--tc-f5a623))" }}>// STRUTTURE DEL CLUB</span>
                <span className="op-mono text-[10px] text-t8f95a3 uppercase">livello 4 · fase 2</span>
            </div>

            <div className="p-5 space-y-4">
                {!prereq_ok && (
                    <div className="op-mono text-[11px] leading-relaxed border border-t22252a bg-t0f1115 p-3 text-t8f95a3" data-testid="structures-locked">
                        🔒 {prereq_label}
                    </div>
                )}
                {can_manage && prereq_ok && (
                    <div className="op-mono text-[10px] leading-relaxed border border-t22252a bg-t0f1115 p-2.5" data-testid="structures-hint">
                        {controlled_admin ? (
                            <span className="text-tff3b30">🔒 Squadra in Amministrazione Controllata: acquisti bloccati.</span>
                        ) : clubDevLocked ? (
                            <span className="text-tff9d2b">🔒 Hai già fatto un intervento di sviluppo club (stadio o struttura) questa stagione. Prossima stagione potrai farne un altro.</span>
                        ) : !summer_open ? (
                            <span className="text-t8f95a3">🔒 Le strutture si acquistano solo a mercato di inizio stagione (finestra estiva aperta).</span>
                        ) : (
                            <span className="text-te8ff00">✓ Mercato estivo aperto: puoi acquistare le strutture del Livello 4 (budget → Cassa OP).</span>
                        )}
                    </div>
                )}
                {err && <div className="border border-tff3b30 bg-tff3b30/10 text-tff3b30 op-mono text-[11px] p-2" data-testid="structures-err">{err}</div>}
                {msg && <div className="border border-t2ecc71 bg-t2ecc71/10 text-t2ecc71 op-mono text-[11px] p-2" data-testid="structures-msg">{msg}</div>}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {structures.map((s) => {
                        const meta = STRUCT_META[s.key] || { icon: "•", accent: "rgb(var(--tc-f5a623))" };
                        const canAfford = (budget || 0) >= s.build_cost;
                        const disabled = !summer_open || controlled_admin || clubDevLocked || !canAfford || !prereq_ok || busy === `buy:${s.key}`;
                        return (
                            <div
                                key={s.key}
                                className={`border p-3 transition-opacity ${s.owned ? "border-tf5a623/50 bg-tf5a623/[0.05]" : "border-t1a1d24"} ${!prereq_ok && !s.owned ? "opacity-50" : ""}`}
                                data-testid={`structure-card-${s.key}`}
                            >
                                <div className="flex justify-between items-start mb-1">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg" style={{ color: meta.accent }}>{meta.icon}</span>
                                        <div className="op-mono text-[13px] text-tffffff">{s.label}</div>
                                    </div>
                                    <div className={`op-mono text-[9px] px-2 py-0.5 border ${s.owned ? "text-tf5a623 border-tf5a623/50 bg-tf5a623/[0.08]" : "text-t5c6370 border-t22252a"}`}>
                                        {s.owned ? "✓ ATTIVA" : "non attiva"}
                                    </div>
                                </div>
                                <div className="op-mono text-[10px] text-tc9ccd3 leading-relaxed mb-2">{s.benefit}</div>
                                <div className="op-mono text-[10px] text-t5c6370 mb-2">
                                    Acquisto <span className="text-tffffff">{fmtM(s.build_cost)}</span> · Annuale <span className="text-tffffff">{fmtM(s.annual_cost)}</span>
                                </div>
                                {can_manage && !s.owned && (
                                    <button
                                        onClick={() => buy(s)}
                                        disabled={disabled}
                                        className="w-full op-mono text-[11px] uppercase tracking-wider py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                        style={{ borderColor: meta.accent, color: meta.accent }}
                                        title={!prereq_ok ? "Serve il Livello 3A" : controlled_admin ? "Amministrazione Controllata: bloccato" : clubDevLocked ? "Già fatto un intervento di sviluppo club questa stagione" : !summer_open ? "Solo a mercato estivo aperto" : !canAfford ? "Budget insufficiente" : `Acquista ${s.label}`}
                                        data-testid={`structure-buy-${s.key}`}
                                    >
                                        {busy === `buy:${s.key}` ? "..." : !canAfford ? `Budget insufficiente (${fmtM(s.build_cost)})` : `Acquista (${fmtM(s.build_cost)})`}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Centro Medico — cura infortuni */}
                {medical_center?.owned && can_manage && (
                    <div className="border-t border-t22252a pt-4" data-testid="medical-panel">
                        <div className="flex items-center justify-between mb-2">
                            <div className="op-mono text-[11px] text-t2ecc71 uppercase">➕ Centro Medico · Cura infortuni</div>
                            <div className="op-mono text-[9px] text-t8f95a3">
                                {medical_center.used_this_giornata
                                    ? "Già usato questa giornata"
                                    : current_giornata != null ? `Disponibile · Giornata ${current_giornata}` : "Nessuna giornata in corso"}
                            </div>
                        </div>
                        {injuries.length === 0 ? (
                            <div className="op-mono text-[11px] text-t5c6370">Nessun giocatore infortunato. 🎉</div>
                        ) : (
                            <div className="space-y-2">
                                {injuries.map((inj) => {
                                    const disabled = medical_center.used_this_giornata || current_giornata == null || busy === `med:${inj.id}`;
                                    return (
                                        <div key={inj.id} className="flex items-center justify-between border border-t1a1d24 p-2" data-testid={`medical-injury-${inj.id}`}>
                                            <div className="op-mono text-[11px] text-tffffff">
                                                {inj.player_name}
                                                {inj.position && <span className="text-t5c6370 ml-1">· {trPositionShort(inj.position)}</span>}
                                                <span className="text-tff7a1a ml-2">{inj.weeks_remaining}g</span>
                                            </div>
                                            <button
                                                onClick={() => runMedical(inj)}
                                                disabled={disabled}
                                                className="op-mono text-[10px] uppercase tracking-wider px-2.5 py-1 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                                style={{ borderColor: "rgb(var(--tc-2ecc71))", color: "rgb(var(--tc-2ecc71))" }}
                                                title={medical_center.used_this_giornata ? "Già usato questa giornata" : "Riduci di 1 giornata"}
                                                data-testid={`medical-use-${inj.id}`}
                                            >
                                                {busy === `med:${inj.id}` ? "..." : "Cura −1 giornata"}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
